import type { PayloadAction } from '@reduxjs/toolkit';
import type { ClaimSetCreationData, ClaimSetCreationIncidentType } from '@/domains/claimSets/types/ClaimSetCreation';
import type { ClaimSetCreationSuccessCreationResponse } from '@/domains/claimSets/types/api/responses/ClaimSetCreationSuccessCreationResponse';
import type { SelectOption } from '@/features/editMode/types/selectOption';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import type { RootState } from '@/store/types/RootState';
import { Link } from 'react-router-dom';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import Typography from '@mui/material/Typography';
import { ClaimSetCreationStep } from '@/features/serviceModal/components/ClaimSetCreationModal/enums/ClaimSetCreationStep';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import ClaimSetEvents from '@/domains/claimSets/events';
import {
  completeClaimSetCreation,
  completeClaimSetCreationSuccess,
  getClaimSetCreationOptions,
  getClaimSetCreationOptionsSuccess,
  reportClaimSubmit,
  resetClaimSetCreationStatuses,
} from '@/domains/claimSets/store/claimSetCreationSlice';
import { RootPaths } from '@/routes/paths';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

function* reportClaimSubmitSaga({ payload: _payload }: PayloadAction<{ formId: string; data: any }>) {
  try {
    // const keyValuePair = Object.entries(data.fields);
    // const result = keyValuePair.reduce((result = {}, [key, value]) => {
    //   return {
    //     ...result,
    //     [key]: value?.['value'],
    //   };
    // }, {});

    // TODO test it later after submitting that feathery works fine on dev environment
    yield true;

    EventService.dispatch(new ClaimSetEvents.Reported({}));
  } catch (error) {
    console.error(error);
  }
}

function* getClaimSetCreationOptionsSaga() {
  try {
    const [{ data: claimTypeOptions }]: [HttpServiceResponse<SelectOption[]>] = yield all([
      call(HttpService.get, '/examiner/update-options/claim-types'),
    ]);

    yield put(
      getClaimSetCreationOptionsSuccess({
        claimTypes: claimTypeOptions,
      }),
    );
  } catch (error) {
    yield put(resetClaimSetCreationStatuses());
    console.error(error);
    ModalService.close(Modals.ClaimSetCreation);
  }
}

function* completeClaimSetCreationSaga() {
  yield put(setSystemLoadingStatus(true));

  try {
    const formData: ClaimSetCreationData = yield select((state: RootState) => state.claimSetCreation.form);
    const IncidentTypeId = formData?.[ClaimSetCreationStep.IncidentType]?.IncidentTypeId;

    const {
      data: { claimSetNumber },
    }: HttpServiceResponse<ClaimSetCreationSuccessCreationResponse> = yield call(
      HttpService.post,
      '/examiner/claim-sets/create-claim-set',
      {
        IncidentTypeId: IncidentTypeId,
      } as ClaimSetCreationIncidentType,
    );

    if (claimSetNumber) {
      SnacksService.add({
        type: SnackType.Success,
        title: (
          <Typography variant="body1Bold">
            <Link
              data-testid="SnackCreatedClaimSetLink"
              to={RootPaths.CLAIM_SET.replace(':claimSetNumber', String(claimSetNumber))}
              replace>
              Claim Set {claimSetNumber}
            </Link>{' '}
            was successfully created
          </Typography>
        ),
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
    }

    yield put(completeClaimSetCreationSuccess());
  } catch (error) {
    console.error(error);
    yield put(resetClaimSetCreationStatuses());
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
    ModalService.close(Modals.ClaimSetCreation);
  }
}

export function* claimSetCreationWatcher() {
  yield takeLatest(reportClaimSubmit.type, reportClaimSubmitSaga);
  yield takeLatest(getClaimSetCreationOptions.type, getClaimSetCreationOptionsSaga);
  yield takeLatest(completeClaimSetCreation.type, completeClaimSetCreationSaga);
}
